import React from 'react';

export const STANDARD_PRICE_PER_LOAN = 99;
export const DEFAULT_INTRO_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const todayDateString = () => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');

    return `${now.getFullYear()}-${mm}-${dd}`;
};

const addDays = (dateString, days) => {
    const date = new Date(`${dateString}T00:00:00Z`);

    return new Date(date.getTime() + days * MS_PER_DAY).toISOString().slice(0, 10);
};

const daysBetween = (from, to) =>
    Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY);

const clampDiscount = (pct) => Math.min(Math.max(pct / 100, 0), 1);

export const calculatedPrice = (form) => form.basePricePerLoan * (1 - clampDiscount(form.discountRate));

/** Form state for the per-loan fields, from an org's `billing` map (or defaults for a new one). */
export const perLoanFormFromBilling = (billing = {}) => {
    const hasIntroPrice = typeof billing.introPricePerLoan === 'number' && Boolean(billing.introPriceEndDate);
    const startDate = billing.billingStartDate || '';

    return {
        billingMode: billing.billingMode || (billing.pricePerLoan !== undefined ? 'auto_charge' : 'invoice'),
        basePricePerLoan: billing.basePricePerLoan ?? STANDARD_PRICE_PER_LOAN,
        discountRate: billing.discountRate != null ? billing.discountRate * 100 : 0,
        pricePerLoan: billing.pricePerLoan ?? STANDARD_PRICE_PER_LOAN,
        billingStartDate: startDate,
        hasIntroPrice,
        introPricePerLoan: hasIntroPrice ? billing.introPricePerLoan : 0,
        introDays: hasIntroPrice && startDate ? daysBetween(startDate, billing.introPriceEndDate) : DEFAULT_INTRO_DAYS,
    };
};

/** Returns an error message, or null when the form can be saved. */
export const validatePerLoanForm = (form) => {
    const finalPrice = form.pricePerLoan > 0 ? form.pricePerLoan : calculatedPrice(form);
    if (finalPrice <= 0) return 'Effective price per loan must be greater than zero';
    if (!form.hasIntroPrice) return null;
    if (!form.billingStartDate) return 'Pick the date billing starts so the intro price has a start';
    if (!(form.introDays > 0)) return 'Intro period must be at least one day';
    if (!(form.introPricePerLoan >= 0)) return 'Intro price cannot be negative';

    return null;
};

/** The `billing` fields the per-loan form writes. */
export const buildPerLoanBilling = (form) => {
    const finalPrice = form.pricePerLoan > 0 ? form.pricePerLoan : calculatedPrice(form);

    return {
        billingType: 'per_loan',
        billingMode: form.billingMode,
        basePricePerLoan: Number(form.basePricePerLoan),
        discountRate: clampDiscount(form.discountRate),
        pricePerLoan: Number(finalPrice),
        billingStartDate: form.billingStartDate || null,
        introPricePerLoan: form.hasIntroPrice ? Number(form.introPricePerLoan) : null,
        introPriceEndDate: form.hasIntroPrice ? addDays(form.billingStartDate, Number(form.introDays)) : null,
    };
};

export const describePerLoanForm = (form) => {
    const finalPrice = form.pricePerLoan > 0 ? form.pricePerLoan : calculatedPrice(form);
    const start = form.billingStartDate === todayDateString() ? 'today' : form.billingStartDate || 'every funded loan';
    const regular = `$${Number(finalPrice).toFixed(2)} per funded loan`;
    const mode = form.billingMode === 'invoice' ? 'Net-30 invoice' : 'card on file';
    if (!form.hasIntroPrice) return `${regular}, starting ${start}, ${mode}.`;

    return `$${Number(form.introPricePerLoan).toFixed(2)} per funded loan for the first ${form.introDays} days, then ${regular}; starting ${start}, ${mode}.`;
};

export const BillingModeSelect = ({ value, onChange }) => (
    <div className="col-md-6">
        <label className="form-label fw-bold">Billing Mode</label>
        <select className="form-select" value={value} onChange={(e) => onChange(e.target.value)}>
            <option value="invoice">Invoice (Net-30)</option>
            <option value="auto_charge">Auto Charge (card on file)</option>
        </select>
        <small className="text-muted">
            {value === 'auto_charge'
                ? 'Stripe will automatically charge the card on file.'
                : 'Customer receives a Stripe invoice with 30 days to pay.'}
        </small>
    </div>
);

const PerLoanPricingFields = ({ form, onChange }) => {
    const update = (patch) => onChange({ ...form, ...patch });
    const calculated = calculatedPrice(form);
    const introEndDate =
        form.hasIntroPrice && form.billingStartDate && form.introDays > 0
            ? addDays(form.billingStartDate, Number(form.introDays))
            : null;

    return (
        <>
            <div className="col-md-6">
                <label className="form-label fw-bold">Base Price Per Loan ($)</label>
                <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                        type="number"
                        className="form-control"
                        min="0"
                        step="0.01"
                        value={form.basePricePerLoan}
                        onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            update({
                                basePricePerLoan: val,
                                pricePerLoan: parseFloat((val * (1 - clampDiscount(form.discountRate))).toFixed(2)),
                            });
                        }}
                    />
                </div>
                <small className="text-muted">List price before any discount.</small>
            </div>

            <div className="col-md-6">
                <label className="form-label fw-bold">Discount Rate (%)</label>
                <div className="input-group">
                    <input
                        type="number"
                        className="form-control"
                        min="0"
                        max="100"
                        step="1"
                        value={form.discountRate}
                        onChange={(e) => {
                            const pct = parseFloat(e.target.value) || 0;
                            update({
                                discountRate: pct,
                                pricePerLoan: parseFloat((form.basePricePerLoan * (1 - clampDiscount(pct))).toFixed(2)),
                            });
                        }}
                    />
                    <span className="input-group-text">%</span>
                </div>
                <small className="text-muted">Negotiated discount for this customer.</small>
            </div>

            <div className="col-md-6">
                <label className="form-label fw-bold">Effective Price Per Loan ($)</label>
                <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                        type="number"
                        className="form-control"
                        min="0"
                        step="0.01"
                        value={form.pricePerLoan}
                        onChange={(e) => update({ pricePerLoan: parseFloat(e.target.value) || 0 })}
                    />
                </div>
                <small className="text-muted">
                    Calculated: ${form.basePricePerLoan} × (1 - {form.discountRate}%) = ${calculated.toFixed(2)}
                    {Math.abs(form.pricePerLoan - calculated) > 0.01 && (
                        <span className="text-warning ms-1">(manually overridden)</span>
                    )}
                </small>
            </div>

            <div className="col-md-6">
                <label className="form-label fw-bold">Billing Starts On</label>
                <input
                    type="date"
                    className="form-control"
                    value={form.billingStartDate}
                    onChange={(e) => update({ billingStartDate: e.target.value })}
                />
                <small className="text-muted">Loans funded before this date are not billed. Leave empty to bill every funded loan.</small>
            </div>

            <div className="col-12">
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id="introPriceToggle"
                        checked={form.hasIntroPrice}
                        onChange={(e) =>
                            update({
                                hasIntroPrice: e.target.checked,
                                billingStartDate: e.target.checked && !form.billingStartDate ? todayDateString() : form.billingStartDate,
                            })
                        }
                    />
                    <label className="form-check-label fw-bold" htmlFor="introPriceToggle">
                        Different price for the first days
                    </label>
                </div>
                <small className="text-muted">For example $49 per loan for the first 30 days, then the effective price above.</small>
            </div>

            {form.hasIntroPrice && (
                <>
                    <div className="col-md-6">
                        <label className="form-label fw-bold">Intro Price Per Loan ($)</label>
                        <div className="input-group">
                            <span className="input-group-text">$</span>
                            <input
                                type="number"
                                className="form-control"
                                min="0"
                                step="0.01"
                                value={form.introPricePerLoan}
                                onChange={(e) => update({ introPricePerLoan: parseFloat(e.target.value) || 0 })}
                            />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <label className="form-label fw-bold">For The First</label>
                        <div className="input-group">
                            <input
                                type="number"
                                className="form-control"
                                min="1"
                                step="1"
                                value={form.introDays}
                                onChange={(e) => update({ introDays: parseInt(e.target.value, 10) || 0 })}
                            />
                            <span className="input-group-text">days</span>
                        </div>
                        <small className="text-muted">
                            {introEndDate
                                ? `Loans funded from ${form.billingStartDate} until ${introEndDate} (exclusive) use the intro price.`
                                : 'Counted from the billing start date.'}
                        </small>
                    </div>
                </>
            )}
        </>
    );
};

export default PerLoanPricingFields;
